<script>
import { mapState, mapActions } from "vuex";

import NavigationWrapper from "@/components/NavigationWrapper";
import List from "@/components/List";
import RolePermissionsTable from "@/pages/Team/Roles/Role/RolePermissionsTable";
import DeleteRoleModal from "@/pages/Team/Roles/Role/DeleteModal";
import { defaultHandle } from "@/utils";

export default {
  // Included components
  components: {
    NavigationWrapper,
    RolePermissionsTable,
    DeleteRoleModal,
    List,
  },
  data() {
    return {
      // The ID of the role being displayed
      roleId: null,

      // The actual role object
      role: null,

      // Selected permissions
      selectedPermissions: {
        "team.manage": false,
        "roles.manage": false,
        "billing.manage": false,
        "members.view": false,
        "members.edit": false,
        "workers.view": false,
        "workers.edit": false,
        "groups.view": false,
        "groups.edit": false,
        "alerts.view": false,
        "alerts.edit": false,
      },
    };
  },

  computed: {
    // Mapped vuex state
    ...mapState(["roles", "groups", "permissions"]),
  },

  watch: {
    // When the name changes, update the handle to be the default value,
    // unless the handle has been manually changed to something different
    name(value, old) {
      if (this.handle === "" || this.handle === defaultHandle(old)) {
        this.handle = defaultHandle(value);
      }
    },
  },

  // Initialize the state of the component
  async created() {
    this.roleId = this.$route.params.roleId;
    this.role = this.roles[this.roleId];

    await Promise.all([
      this.getRole({ roleId: this.roleId }),
      this.getPermissions(),
    ]);
    this.role = this.roles[this.roleId];

    this.role.permissions.forEach((permission) => {
      this.selectedPermissions[
        permission.entity + "." + permission.action
      ] = true;
    });
  },

  methods: {
    // Helper methods and mapped vuex actions
    ...mapActions(["getRole", "getPermissions"]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/team">Settings</b-breadcrumb-item>
      <b-breadcrumb-item to="/team/roles">Roles</b-breadcrumb-item>
      <b-breadcrumb-item active>{{ role.name }}</b-breadcrumb-item>
    </template>
    <b-row class="mb-2">
      <b-col>
        <h1>{{ role.name }}</h1>
      </b-col>
      <b-col align-self="center" v-if="!role.read_only">
        <div class="float-right">
          <span id="disabled-wrapper" class="d-inline-block" tabindex="0">
            <b-button
              :disabled="role.account_count > 0"
              class="mr-3"
              variant="danger"
              v-b-modal.delete-modal
            >
              Delete
            </b-button>
          </span>
          <b-tooltip v-if="role.account_count > 0" target="disabled-wrapper">
            You cannot delete this role while it is assigned to members.
          </b-tooltip>
          <DeleteRoleModal :role-id="roleId" modal-id="delete-modal" />
          <b-button variant="primary" :to="'/team/roles/' + role.id + '/edit'">
            Edit
          </b-button>
        </div>
      </b-col>
      <b-col cols="12" class="notes">
        {{ role.description }}
        <span
          v-if="role.limited_group_access && role.accessible_groups.length == 0"
        >
          Access to no groups.
        </span>
        <span v-else-if="role.limited_group_access">
          Access limited to only group{{
            role.accessible_groups.length == 1 ? "" : "s"
          }}
          <List :items="role.accessible_groups">
            <template #default="{ item }">
              <b-link :to="`/groups/${item.id}`">{{ item.name }}</b-link>
            </template>
          </List>
          .
        </span>
        <span v-else>Access to all groups.</span>
      </b-col>
    </b-row>

    <b-card class="Card-table mb-3">
      <RolePermissionsTable v-model="selectedPermissions" disabled />
    </b-card>
  </NavigationWrapper>
</template>

<style scoped>
.notes {
  color: #6c757d;
}
</style>
